import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import type { TLookup } from '@/types/lookup';

import { lookupQuery } from '@/queries/lookup';

const resolveAmenityLabel = (item: Pick<TLookup, 'key' | 'value'> | undefined, fallback: string) =>
  item?.value?.trim() || fallback;

const EMPTY_LOOKUPS: TLookup[] = [];

/**
 * Amenity options and labels for the station amenities form and the Visits pages.
 * Data: the `amenities` lookup (`lookupQuery`), loaded once per session and shared by every caller.
 * `options` is empty until the lookup loads; `getAmenityLabel(key)` falls back to the key.
 * Constraint: the saved lookup rows are shared and never changed here (the sort works on a copy).
 */
export const useAmenities = () => {
  const { data, isSuccess, isLoading } = useQuery(lookupQuery({ category: 'amenities' }));

  const lookups = data ?? EMPTY_LOOKUPS;

  const labelsByKey = useMemo(() => {
    const labels: Record<string, string> = {};

    lookups.forEach(({ key, value }) => {
      const label = value?.trim();

      if (label) {
        labels[key] = label;
      }
    });

    return labels;
  }, [lookups]);

  const getAmenityLabel = useCallback((key: string) => labelsByKey[key] || key, [labelsByKey]);

  const options = useMemo(() => {
    if (!isSuccess) {
      return [];
    }

    return lookups
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(({ key, value, attributes }) => ({
        name: key,
        label: resolveAmenityLabel({ key, value }, key),
        nested:
          attributes && 'children' in attributes
            ? attributes.children?.map((childKey) => {
                const child = lookups.find((item) => item.key === childKey);

                return {
                  name: childKey,
                  label: resolveAmenityLabel(child, childKey),
                };
              })
            : undefined,
      }));
  }, [lookups, isSuccess]);

  return {
    options,
    loading: isLoading,
    getAmenityLabel,
  };
};
